import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { businessesAPI, advertisersAPI, supportersAPI } from '../api';
import axios from 'axios';
import {
  FileText, Users, Eye, BarChart3, Clock, Mail, CalendarClock, Search,
  AlertTriangle, CheckCircle2, ImageOff, Wrench, Sparkles, Calendar, Home,
  Plus, Briefcase, Megaphone, Heart, Handshake, MessageSquare, ArrowRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCENTS = {
  sky: { bg: 'bg-sky-50', text: 'text-sky-500' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-500' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-500' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-500' },
};

const MetricCard = ({ icon: Icon, label, value, accent = 'sky', to, testid }) => {
  const a = ACCENTS[accent] || ACCENTS.sky;
  const card = (
    <div
      className="bg-white border border-slate-200 shadow-sm rounded-xl p-5 hover:shadow-md transition-all duration-200 h-full"
      data-testid={testid}
    >
      <div className={`w-9 h-9 rounded-lg ${a.bg} flex items-center justify-center mb-3`}>
        <Icon size={17} className={a.text} />
      </div>
      <p className="text-2xl font-bold text-slate-900 tracking-tight tabular-nums">{value}</p>
      <p className="text-xs font-medium text-slate-500 mt-0.5">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
};

const AdminDashboard = () => {
  const token = localStorage.getItem('admin_token');
  const authHeader = { Authorization: `Bearer ${token}` };

  const [counts, setCounts] = useState({ posts: 0, businesses: 0, advertisers: 0, supporters: 0, featuredPartners: 0, properties: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [topPosts, setTopPosts] = useState([]);
  const [seoHealth, setSeoHealth] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [postsCount, biz, ads, sup, fp, analyticsRes, topRes, seoRes, propsRes] = await Promise.all([
        axios.get(`${API}/api/posts/count/all`),
        businessesAPI.getAll(),
        advertisersAPI.getAll(),
        supportersAPI.getAll(),
        axios.get(`${API}/api/featured-partners`),
        axios.get(`${API}/api/analytics/overview`, { headers: authHeader }).catch(() => ({ data: null })),
        axios.get(`${API}/api/analytics/top-posts?limit=6`, { headers: authHeader }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/analytics/seo-health`, { headers: authHeader }).catch(() => ({ data: null })),
        axios.get(`${API}/api/admin/properties`, { headers: authHeader }).catch(() => ({ data: [] })),
      ]);
      setCounts({
        posts: postsCount.data.total,
        businesses: biz.data.length,
        advertisers: ads.data.length,
        supporters: sup.data.length,
        featuredPartners: fp.data.length,
        properties: (propsRes.data || []).filter((p) => p.active).length,
      });
      setAnalytics(analyticsRes.data);
      setTopPosts(topRes.data || []);
      setSeoHealth(seoRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const collections = [
    { title: 'Posts', count: counts.posts, icon: FileText, link: '/admin/posts' },
    { title: 'Properties', count: counts.properties, icon: Home, link: '/admin/properties' },
    { title: 'Businesses', count: counts.businesses, icon: Briefcase, link: '/admin/businesses' },
    { title: 'Advertisers', count: counts.advertisers, icon: Megaphone, link: '/admin/advertisers' },
    { title: 'Supporters', count: counts.supporters, icon: Heart, link: '/admin/supporters' },
    { title: 'Partners', count: counts.featuredPartners, icon: Handshake, link: '/admin/featured-partners' },
  ];

  const quickActions = [
    { label: 'New Post', to: '/admin/posts/new', icon: Plus, primary: true, testid: 'quick-new-post' },
    { label: 'Add Event', to: '/admin/events', icon: Calendar, testid: 'quick-add-event' },
    { label: 'Send Broadcast', to: '/admin/broadcast', icon: Mail, testid: 'quick-send-broadcast' },
    { label: 'AI Queue', to: '/admin/ai-queue', icon: Sparkles, testid: 'ai-queue-link' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard-page">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          What's happening on paisley.org.uk right now.
        </p>
      </div>

      {/* Hero metrics */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="analytics-overview">
          <MetricCard icon={Eye} label="Total views" value={(analytics.total_views || 0).toLocaleString()} accent="sky" testid="metric-total-views" />
          <MetricCard icon={FileText} label="Published posts" value={(analytics.published || 0).toLocaleString()} accent="emerald" testid="metric-published" />
          <MetricCard icon={Users} label="Subscribers" value={(analytics.subscribers || 0).toLocaleString()} accent="violet" to="/admin/subscribers" testid="metric-subscribers" />
          <MetricCard icon={MessageSquare} label="Messages" value={(analytics.contact_messages || 0).toLocaleString()} accent="amber" to="/admin/messages" testid="metric-messages" />
        </div>
      )}

      {/* Secondary metrics: workflow states */}
      {analytics && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Link to="/admin/posts?status=scheduled" className="bg-white border border-slate-200 shadow-sm rounded-xl px-4 py-3 flex items-center gap-3 hover:border-amber-200 hover:shadow-md transition-all" data-testid="stat-scheduled-link">
            <CalendarClock size={16} className="text-amber-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-lg font-bold text-amber-600 leading-tight tabular-nums">{analytics.scheduled || 0}</p>
              <p className="text-[11px] text-slate-500 font-medium">Scheduled</p>
            </div>
          </Link>
          <Link to="/admin/posts?status=draft" className="bg-white border border-slate-200 shadow-sm rounded-xl px-4 py-3 flex items-center gap-3 hover:border-sky-200 hover:shadow-md transition-all" data-testid="stat-drafts-link">
            <Clock size={16} className="text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-lg font-bold text-slate-900 leading-tight tabular-nums">{analytics.drafts || 0}</p>
              <p className="text-[11px] text-slate-500 font-medium">Drafts</p>
            </div>
          </Link>
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl px-4 py-3 flex items-center gap-3" data-testid="stat-total-posts">
            <BarChart3 size={16} className="text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-lg font-bold text-slate-900 leading-tight tabular-nums">{(analytics.total_posts || 0).toLocaleString()}</p>
              <p className="text-[11px] text-slate-500 font-medium">Total posts</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="bg-white/60 backdrop-blur-xl border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-2xl p-3 flex flex-wrap items-center gap-2 mb-8" data-testid="quick-actions">
        {quickActions.map(({ label, to, icon: Icon, primary, testid }) => (
          <Link key={to + label} to={to}>
            <Button
              size="sm"
              variant={primary ? 'default' : 'outline'}
              className={primary
                ? 'bg-sky-500 hover:bg-sky-400 text-white rounded-lg shadow-sm'
                : 'rounded-lg bg-white/70'}
              data-testid={testid}
            >
              <Icon size={15} className="mr-1.5" /> {label}
            </Button>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* SEO Health */}
        {seoHealth && (
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="seo-health-widget">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Search size={16} className="text-sky-500" /> SEO Health
              </h3>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                seoHealth.health_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                seoHealth.health_score >= 50 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`} data-testid="seo-health-score">
                {seoHealth.health_score}/100
              </span>
            </div>

            <div className="mb-5">
              <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                <span>SEO-ready posts</span>
                <span>{seoHealth.seo_ready.toLocaleString()} / {seoHealth.published.toLocaleString()}</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    seoHealth.health_score >= 80 ? 'bg-emerald-500' :
                    seoHealth.health_score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${seoHealth.health_score}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5 mb-4 text-center">
              <div className="bg-slate-50 rounded-lg p-2.5" data-testid="seo-stat-sitemap">
                <div className="text-lg font-bold text-slate-900 tabular-nums">{seoHealth.in_sitemap.toLocaleString()}</div>
                <div className="text-[10px] text-slate-500">In sitemap</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2.5" data-testid="seo-stat-recent">
                <div className="text-lg font-bold text-slate-900 tabular-nums">{seoHealth.recent_30d.toLocaleString()}</div>
                <div className="text-[10px] text-slate-500">Last 30 days</div>
              </div>
            </div>

            {(seoHealth.broken_image > 0 || seoHealth.missing_image > 0 || seoHealth.missing_excerpt > 0) ? (
              <div className="border-t border-slate-100 pt-4 space-y-2">
                <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <AlertTriangle size={13} className="text-amber-500" /> Issues to fix
                </p>
                {seoHealth.broken_image > 0 && (
                  <p className="text-xs text-slate-600 flex items-center justify-between" data-testid="seo-issue-broken-image">
                    <span className="flex items-center gap-1.5"><ImageOff size={13} className="text-red-500" /> Broken images</span>
                    <span className="font-bold text-red-600">{seoHealth.broken_image.toLocaleString()}</span>
                  </p>
                )}
                {seoHealth.missing_image > 0 && (
                  <p className="text-xs text-slate-600 flex items-center justify-between" data-testid="seo-issue-missing-image">
                    <span className="flex items-center gap-1.5"><ImageOff size={13} className="text-amber-500" /> No featured image</span>
                    <span className="font-bold text-amber-600">{seoHealth.missing_image.toLocaleString()}</span>
                  </p>
                )}
                {seoHealth.missing_excerpt > 0 && (
                  <p className="text-xs text-slate-600 flex items-center justify-between" data-testid="seo-issue-missing-excerpt">
                    <span className="flex items-center gap-1.5"><FileText size={13} className="text-amber-500" /> No excerpt</span>
                    <span className="font-bold text-amber-600">{seoHealth.missing_excerpt.toLocaleString()}</span>
                  </p>
                )}
                <Link to="/admin/maintenance" className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700 mt-1" data-testid="seo-fix-tools-link">
                  <Wrench size={12} /> Open maintenance tools <ArrowRight size={11} />
                </Link>
              </div>
            ) : (
              <div className="border-t border-slate-100 pt-4 flex items-center gap-2 text-xs text-emerald-700">
                <CheckCircle2 size={14} className="text-emerald-500" /> No outstanding SEO issues.
              </div>
            )}
          </div>
        )}

        {/* Top posts */}
        <div className="lg:col-span-2 bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="top-posts-panel">
          <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 size={16} className="text-sky-500" /> Top posts
          </h3>
          {topPosts.length === 0 ? (
            <p className="text-slate-400 text-sm">Views will appear as visitors read posts.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {topPosts.map((post, i) => (
                <Link to={`/admin/posts/edit/${post.id}`} key={post.id} className="flex items-center gap-3 py-2.5 group">
                  <span className="text-xs font-bold text-slate-300 w-5 text-right tabular-nums">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 group-hover:text-sky-600 transition-colors truncate">{post.title}</p>
                  </div>
                  <span className="text-[11px] text-slate-400 capitalize hidden sm:block">{post.category}</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1 shrink-0 tabular-nums">
                    <Eye size={11} /> {(post.views || 0).toLocaleString()}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content overview */}
      <div data-testid="content-overview">
        <h3 className="text-base font-bold text-slate-900 mb-3">Content at a glance</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {collections.map(({ title, count, icon: Icon, link }) => (
            <Link to={link} key={title}>
              <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-4 hover:shadow-md hover:border-sky-200 transition-all group h-full">
                <Icon size={16} className="text-slate-400 group-hover:text-sky-500 transition-colors mb-2" />
                <p className="text-xl font-bold text-slate-900 tabular-nums">{count.toLocaleString()}</p>
                <p className="text-[11px] font-medium text-slate-500">{title}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
